/**
 * ShortcutSheet — the F1 help sheet of every back-office screen
 * (design/ux/01_foundations.md §4.6, WP-17; generalises
 * components/pos/ShortcutCheatSheet.jsx, which the POS tills keep as is).
 *
 *  · Title « Raccourcis clavier — {page} ». Two columns: « Cet écran » (the
 *    screen's registered map, grouped as `posScreenShortcutGroups` does) and
 *    « Partout » (GLOBAL_SHORTCUTS). Only bindings that have a handler right
 *    now are listed — the sheet documents what actually fires.
 *  · A dialog (focus trap, Échap closes, F1 toggles through the dispatcher);
 *    focus starts on « Filtrer les raccourcis » (accent-insensitive).
 *  · « Imprimer l'aide » prints an A4 sheet to pin next to the screen, through
 *    the print layer (WP-19: PrintDocument + PrintTable from the print root).
 *
 * Props: `open`, `onClose`, `data` (a registry snapshot), `pageTitle`.
 */
import * as React from "react";
import { Button, Input, Kbd, Modal } from "@/components/ui/primitives";
import { shortcutGroups } from "@/config/shortcuts.config.js";
import { foldText, queryTokens } from "@/lib/combobox.js";
import { PrintDocument, PrintTable, printElement } from "@/components/print";
import { useAuthStore } from "@/stores/authStore.js";

export interface SheetBinding {
  id: string;
  key: string;
  label: string;
  group?: string;
}

export interface ShortcutSheetData {
  title: string | null;
  screen: ReadonlyArray<SheetBinding>;
  global: ReadonlyArray<SheetBinding>;
}

export interface ShortcutSheetProps {
  open: boolean;
  onClose: () => void;
  data: ShortcutSheetData;
  /** Fallback heading when the screen map has no title (the page h1). */
  pageTitle?: string | null;
}

function filterBindings(list: ReadonlyArray<SheetBinding>, query: string) {
  const tokens = queryTokens(query);
  if (tokens.length === 0) return list;
  return list.filter((b) => {
    const hay = foldText(`${b.label} ${b.key} ${b.group ?? ""}`);
    return tokens.every((t) => hay.includes(t));
  });
}

const PRINT_COLUMNS = [
  { id: "group", label: "Rubrique", width: "25%" },
  { id: "label", label: "Action" },
  { id: "key", label: "Touche", kind: "code" as const, width: "22%" },
];

/** The printable aid: one A4 page, « Cet écran » then « Partout », the shortcuts of the moment. */
export function ShortcutSheetDocument({
  heading,
  data,
  pharmacie,
}: {
  heading: string | null;
  data: ShortcutSheetData;
  pharmacie?: { nom?: string | null; logo_url?: string | null } | null;
}) {
  const rows = (list: ReadonlyArray<SheetBinding>) =>
    shortcutGroups(list).flatMap(({ group, shortcuts }) =>
      shortcuts.map((s: SheetBinding) => ({ ...s, group, rowId: `${group}-${s.id}` })),
    );
  return (
    <PrintDocument
      title="Raccourcis clavier"
      numero={null}
      meta={heading ? [{ label: "Écran", value: heading }] : []}
      pharmacie={pharmacie ?? null}
      printTitle={heading ? `Raccourcis clavier — ${heading}` : "Raccourcis clavier"}
    >
      <PrintTable
        caption="Cet écran"
        columns={PRINT_COLUMNS}
        rows={rows(data.screen)}
        getRowId={(r: any) => r.rowId}
        emptyText="Cet écran n'a pas de raccourci propre : les raccourcis « Partout » s'appliquent."
      />
      <PrintTable caption="Partout" columns={PRINT_COLUMNS} rows={rows(data.global)} getRowId={(r: any) => r.rowId} />
    </PrintDocument>
  );
}

function printSheet(heading: string | null, data: ShortcutSheetData) {
  const pharmacie = (useAuthStore as any).getState?.()?.user?.pharmacie ?? null;
  return printElement(<ShortcutSheetDocument heading={heading} data={data} pharmacie={pharmacie} />);
}

const Column: React.FC<{ heading: string; list: ReadonlyArray<SheetBinding>; empty: string }> = ({
  heading,
  list,
  empty,
}) => (
  <section className="pk-keys-sheet-col" aria-label={heading}>
    <h3>{heading}</h3>
    {list.length === 0 ? (
      <p className="pk-keys-sheet-empty">{empty}</p>
    ) : (
      shortcutGroups(list).map(({ group, shortcuts }) => (
        <div key={group}>
          <div className="pk-keys-sheet-group">{group}</div>
          <ul className="pk-keys-sheet-list">
            {shortcuts.map((s: SheetBinding) => (
              <li key={`${group}-${s.id}`} className="pk-keys-sheet-row">
                <span>{s.label}</span>
                <Kbd>{s.key}</Kbd>
              </li>
            ))}
          </ul>
        </div>
      ))
    )}
  </section>
);

export const ShortcutSheet: React.FC<ShortcutSheetProps> = ({ open, onClose, data, pageTitle }) => {
  const [query, setQuery] = React.useState("");
  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);
  const heading = data.title ?? pageTitle ?? null;
  const screen = filterBindings(data.screen, query);
  const global = filterBindings(data.global, query);
  return (
    <Modal
      open={open}
      onClose={onClose}
      dirty={false}
      size="lg"
      title={heading ? `Raccourcis clavier — ${heading}` : "Raccourcis clavier"}
      footer={
        <>
          <span className="pk-keys-sheet-empty">F1 ou Échap pour fermer</span>
          <Button variant="secondary" icon="printer" onClick={() => printSheet(heading, data)}>
            Imprimer l'aide
          </Button>
        </>
      }
    >
      {/* F-keys typed here never reach a legacy page listener underneath. */}
      <div
        onKeyDown={(e) => {
          if (/^F([2-9]|1[0-2])$/.test(e.key)) e.stopPropagation();
        }}
      >
        <div className="pk-keys-sheet-filter">
          <Input
            icon="search"
            type="search"
            data-autofocus=""
            aria-label="Filtrer les raccourcis"
            placeholder="Filtrer les raccourcis"
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          />
        </div>
        <div className="pk-keys-sheet-cols">
          <Column
            heading="Cet écran"
            list={screen}
            empty={
              query
                ? "Aucun raccourci de cet écran ne correspond."
                : "Cet écran n'a pas de raccourci propre : les raccourcis « Partout » s'appliquent."
            }
          />
          <Column heading="Partout" list={global} empty="Aucun raccourci ne correspond." />
        </div>
      </div>
    </Modal>
  );
};

export default ShortcutSheet;
